<template>
  <div class="h-full bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white flex flex-col relative w-full">

    <!-- Channel Info Modal -->
    <div v-if="showInfoModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div class="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900 rounded-t-xl">
          <h3 class="text-lg font-bold flex items-center text-slate-900 dark:text-white">
            <RadIcon name="Hash" :size="20" class="text-blue-700 dark:text-blue-400 mr-2" />
            {{ selectedInfoChannel?.name || selectedInfoChannel?.channel_id || 'Channel Info' }}
          </h3>
          <button @click="showInfoModal = false" class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-800">
            <RadIcon name="X" :size="20" />
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6 overflow-y-auto space-y-8 bg-white dark:bg-slate-950">
          <!-- Summary Section -->
          <section>
            <h4 class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-3">Summary</h4>
            <div class="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800">
              <div>
                <p class="text-xs text-gray-400">Project</p>
                <p class="font-medium flex items-center">
                  {{ getProjectName(selectedInfoChannel?.project_id) }}
                  <span v-if="isDefaultChannel(selectedInfoChannel?.id)" class="ml-2 text-[10px] font-bold uppercase tracking-wider text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
                    Default
                  </span>
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-400">Type</p>
                <p class="font-medium capitalize">{{ selectedInfoChannel?.type || 'Standard' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-400">Visibility</p>
                <p class="font-medium">{{ selectedInfoChannel?.is_private ? 'Private' : 'Public' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-400">Channel ID</p>
                <p class="font-mono text-xs">{{ selectedInfoChannel?.channel_id || selectedInfoChannel?.id }}</p>
              </div>
            </div>
          </section>

          <!-- SSH Connection Section -->
          <section>
            <h4 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">SSH Connection</h4>
            <div class="space-y-4">
              <div class="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4">
                <p class="text-sm text-blue-700 dark:text-blue-200">
                  To connect via SSH, you must first install <strong>wstunnel</strong> globally:
                </p>
                <div class="mt-2 flex items-center bg-white dark:bg-gray-800 p-2 rounded border border-blue-100 dark:border-blue-900">
                  <code class="text-xs flex-1">npm install -g wstunnel</code>
                  <button @click="copyToClipboard('npm install -g wstunnel')" class="text-blue-600 hover:text-blue-800 ml-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                  </button>
                </div>
              </div>

              <div>
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">VSCode Remote SSH Config</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Add this to your <code class="bg-gray-100 dark:bg-gray-700 px-1 rounded">~/.ssh/config</code> file.
                  See the <a href="https://code.visualstudio.com/docs/remote/ssh-tutorial" target="_blank" class="text-blue-600 dark:text-blue-400 hover:underline">VSCode SSH Tutorial</a> for more info.
                </p>
                <div class="relative group">
                  <pre class="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto leading-relaxed">Host {{ getSSHHostAlias(selectedInfoChannel) }}
  HostName {{ getSSHHostName(selectedInfoChannel) }}
  User exec
  Port 22
  StrictHostKeyChecking no
  ProxyCommand wstunnel{{ isWindows ? '.cmd' : '' }} -v -t stdio:%h:%p wss://{{ getSSHHostName(selectedInfoChannel) }}
  UserKnownHostsFile /dev/null
  IdentityFile ~/.ssh/id_radsuite_ed25519</pre>
                  <button
                    @click="copyToClipboard(getSSHConfig(selectedInfoChannel))"
                    class="absolute top-2 right-2 p-2 bg-gray-800 text-gray-400 rounded hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </section>

          <!-- Manage Channel Section -->
          <section>
            <h4 class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-3">Manage Channel</h4>
            <div class="space-y-3">
              <div class="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <p class="text-sm font-bold text-slate-900 dark:text-white">Restart Channel</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Restart the channel service and all associated agents.</p>
                </div>
                <RadButton
                  @click="restartChannel(selectedInfoChannel)"
                  :disabled="isRestarting || isShuttingDown"
                  :loading="isRestarting"
                  variant="primary"
                >
                  Restart
                </RadButton>
              </div>

              <div class="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <p class="text-sm font-bold text-slate-900 dark:text-white">Shutdown Channel</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Stop the channel service. It will need to be manually started again.</p>
                </div>
                <RadButton
                  @click="shutdownChannel(selectedInfoChannel)"
                  :disabled="isRestarting || isShuttingDown"
                  :loading="isShuttingDown"
                  variant="outline"
                  class="border-red-500/50 text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/10"
                >
                  Shutdown
                </RadButton>
              </div>
            </div>
          </section>
        </div>

        <!-- Modal Footer -->
        <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end rounded-b-xl">
          <RadButton
            @click="showInfoModal = false"
            variant="ghost"
          >
            Close
          </RadButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'DashboardView'
}
</script>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useWorkspaceManager } from '@/workspace/workspacemanager'
import { useAuthStore } from '@/stores/auth'
import { showSuccess } from '@/utils/toast'
import { RadIcon, RadButton } from '@radsuite/design-system'

const workspace = useWorkspaceManager()
const authStore = useAuthStore()

// Modal State
const showInfoModal = ref(false)
const selectedInfoChannel = ref<any>(null)
const isRestarting = ref(false)
const isShuttingDown = ref(false)
const isWindows = typeof window !== 'undefined' && window.navigator.platform.indexOf('Win') > -1

function openChannelInfo(channel: any) {
  selectedInfoChannel.value = channel
  showInfoModal.value = true
}

async function restartChannel(channel: any) {
  if (!channel || !authStore.account?.id) return

  if (!confirm(`Are you sure you want to restart channel #${channel.name || channel.channel_id}? This will temporarily disconnect all active agents.`)) {
    return
  }

  isRestarting.value = true
  try {
    const token = authStore.session?.access_token
    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin

    const response = await fetch(`${apiUrl}/api/channels/${channel.id}/restart`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Account-Id': authStore.account.id
      },
      body: JSON.stringify({ channelId: channel.channel_id || channel.id })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(errorData.error || `Failed to restart channel: ${response.status}`)
    }

    showSuccess('Channel restart initiated successfully')

    // Reload the iframe after a short delay to allow the container to start
    workspace.reloadChannel(channel.id, 5000)
    showInfoModal.value = false
  } catch (error: any) {
    console.error('Error restarting channel:', error)
    alert(error.message)
  } finally {
    isRestarting.value = false
  }
}

async function shutdownChannel(channel: any) {
  if (!channel || !authStore.account?.id) return

  if (!confirm(`Are you sure you want to shutdown channel #${channel.name || channel.channel_id}? This will stop the service and disconnect all agents.`)) {
    return
  }

  isShuttingDown.value = true
  try {
    const token = authStore.session?.access_token
    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin

    const response = await fetch(`${apiUrl}/api/channels/${channel.id}/shutdown`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Account-Id': authStore.account.id
      },
      body: JSON.stringify({ channelId: channel.channel_id || channel.id })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(errorData.error || `Failed to shutdown channel: ${response.status}`)
    }

    showSuccess('Channel shutdown initiated successfully')
  } catch (error: any) {
    console.error('Error shutting down channel:', error)
    alert(error.message)
  } finally {
    isShuttingDown.value = false
  }
}

function getProjectName(projectId: string) {
  if (!projectId) return 'None'
  const project = workspace.projects.value.find((p: any) => p.id === projectId)
  return project ? project.name : 'Unknown'
}

function isDefaultChannel(channelId: string) {
  return workspace.projects.value.some((p: any) => p.default_channel_id === channelId)
}

function sanitize(str: string): string {
  if (!str) return ''
  return str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

function getSSHHostName(channel: any) {
  if (!channel || !authStore.account?.id) return 'unknown.connect.radsuite.com'

  const accountIdNoDashes = sanitize(authStore.account.id)


  const channelIdNoDashes = sanitize(channel.channel_id || channel.id)
  return `c-${channelIdNoDashes}-${accountIdNoDashes}.connect.radsuite.com`
}

function getSSHHostAlias(channel: any) {
  if (!channel) return 'radsuite-connection'
  const name = sanitize(channel.name || channel.channel_id || 'channel')
  const project = channel.project_id ? sanitize(getProjectName(channel.project_id)) : 'standalone'
  return `${project}-${name}-radsuite`
}

function getSSHConfig(channel: any) {
  const host = getSSHHostAlias(channel)
  const hostname = getSSHHostName(channel)
  const wsSuffix = isWindows ? '.cmd' : ''

  return `Host ${host}
  HostName ${hostname}
  User exec
  Port 22
  StrictHostKeyChecking no
  ProxyCommand wstunnel${wsSuffix} -v -t stdio:%h:%p wss://${hostname}
  UserKnownHostsFile /dev/null
  IdentityFile ~/.ssh/id_radsuite_ed25519`
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    showSuccess('Copied to clipboard!')
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

onMounted(() => {
  workspace.loadData()
})
</script>

<style scoped>
.channel-item {
  @apply px-3 py-2 rounded-md cursor-pointer transition-colors duration-200;
}
</style>